import * as React from 'react';
import Calendar from 'react-calendar';
import dayjs from 'dayjs';
import Select, { MultiValue, StylesConfig } from 'react-select';
import './schedule-reg-modal.scss';
import 'react-calendar/dist/Calendar.css';

import { ICommuteEmpItem, ISchDateItem } from 'types/commuting';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import { ReactComponent as DashIcon } from 'assets/icons/dash.svg';
import { ReactComponent as DotIcon } from 'assets/icons/dot01.svg';
import { ReactComponent as Close } from '../../../assets/images/close.svg';

import TimeInput from 'common/time-input/time-input';
import RadioButton from 'common/radio/radio';
import Button from 'common/button/button';
import Label from 'common/label/label';
import moment from 'moment';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';

interface IProps {
  selectEmpList: ICommuteEmpItem[];
  submitAddSchedule: (
    emps: IScheEmpItem[],
    startTime: string,
    endTime: string,
    schedues: ISchDateItem[],
    allowance: string,
  ) => void;
}

export interface IScheEmpItem {
  value: number;
  label: string;
  retireDate: Date;
}
interface IForm {
  employees: IScheEmpItem[];
  startTime: string;
  endTime: string;
}

const ScheduleRegModal: React.FunctionComponent<IProps> = ({ selectEmpList, submitAddSchedule }) => {
  const { setModalState } = useAlertConfirmStore();

  const [form, setForm] = React.useState<IForm>({
    employees: [],
    startTime: '',
    endTime: '',
  });
  const [schedules, setSchedules] = React.useState<ISchDateItem[]>([]);
  const [allowance, setAllowance] = React.useState<string>('');

  const colourStyles: StylesConfig<any, true> = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '56px',
    }),
    multiValue: (styles, { data }) => {
      return {
        ...styles,
        backgroundColor: '#D6E7E4',
        borderRadius: '50px',
        fontFamily: 'Spoqa Han Sans Neo',
        fontWeight: '400',
        fontSize: '14px',
        color: '#075453',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '5px',
      };
    },
  };

  const selectAllowance = (allowance: string) => {
    setAllowance(allowance);
  };

  // 1.직원선택 시 목록데이터에 추가
  const selectEmployee = (newValue: MultiValue<IScheEmpItem>) => {
    const list = newValue.map((i) => ({ value: i.value, label: i.label, retireDate: i.retireDate }));
    setForm((values) => ({ ...values, employees: list }));
  };

  const timePicker = (event: React.ChangeEvent<HTMLInputElement>, option: 'START' | 'END') => {
    if (option === 'START') {
      setForm((values) => ({ ...values, startTime: event.target.value }));
    }
    if (option === 'END') {
      setForm((values) => ({ ...values, endTime: event.target.value }));
    }
  };

  const removeSchedules = (id: number) => {
    const newArray = schedules.filter((sch) => sch.id !== id);
    setSchedules(newArray);
  };

  // 캘린더 일자 클릭 시 목록에 추가
  const pickSchedules = (date: Date) => {
    if (form.employees.length === 0) {
      return setModalState({
        isModalShow: true,
        title: '추가일정등록',
        desc: `등록할 직원을 선택해 주세요`,
      });
    }

    // 선택한 직원 중 퇴사일이 있는 직원의 경우 퇴사일보다 이후면 선택 못하도록
    const alreadyRetireEmp = form.employees.find((i) => i.retireDate && moment(i.retireDate).isBefore(date));
    if (alreadyRetireEmp) {
      return setModalState({
        isModalShow: true,
        title: '추가일정등록',
        desc: `${alreadyRetireEmp.label}직원은 해당일자에 퇴사 상태입니다. 다른 일자를 선택해 주세요`,
      });
    }

    const newDates = [...schedules];
    const hasDate = newDates.find((sch) => {
      return moment(sch.date).format('YYYYMMDD') === moment(date).format('YYYYMMDD');
    });
    if (newDates.length > 0) {
      // 기존에 추가된 일자 선택 시 반응 없음
      const lastIdItem = newDates.reduce((pv, ac) => {
        if (pv.id < ac.id) return ac;
        return pv;
      });
      if (!hasDate) {
        newDates.push({ date, id: lastIdItem.id + 1 });
      }
    } else {
      newDates.push({ date, id: 1 });
    }
    setSchedules(newDates);
  };

  const clickSumbitBtn = () => {
    if (form.employees.length === 0) {
      return setModalState({
        isModalShow: true,
        title: '추가일정등록',
        desc: `[1.직원선택] 등록할 직원을 선택해 주세요`,
      });
    }
    if (form.startTime === '' || form.endTime === '') {
      return setModalState({
        isModalShow: true,
        title: '추가일정등록',
        desc: `[2.출퇴근 시간] 출퇴근 시간을 모두 입력해 주세요`,
      });
    }
    if (schedules.length === 0) {
      return setModalState({
        isModalShow: true,
        title: '추가일정등록',
        desc: `[3.근무일 입력] 등록할 일자를 선택해 주세요`,
      });
    }
    if (allowance === '') {
      return setModalState({
        isModalShow: true,
        title: '추가일정등록',
        desc: `[4.수당포함여부] 적용될 수당을 선택해 주세요`,
      });
    }

    submitAddSchedule(form.employees, form.startTime, form.endTime, schedules, allowance);
  };

  const empOptions = selectEmpList
    .filter((i) => i.empSeq !== 0)
    .map((i) => {
      return {
        value: i.empSeq,
        label: i.empName,
        retireDate: i.retireDate,
      };
    });

  return (
    <div className="schedule-reg-modal">
      <div className="schedule-reg-modal__content">
        <div className="schedule-reg__body">
          <div className="schedule-reg__select">
            <div className="schedule-reg__label">1.직원선택</div>
            <Select
              className="schedule-reg__dropdown"
              components={{
                IndicatorSeparator: () => null,
                DropdownIndicator: () => (
                  <div style={{ paddingRight: '15px' }}>
                    <ArrowDownIcon />
                  </div>
                ),
                SingleValue: () => (
                  <div style={{ paddingRight: '15px' }}>
                    <ArrowDownIcon />
                  </div>
                ),
              }}
              isSearchable={false}
              placeholder="직원선택"
              options={empOptions}
              value={form.employees}
              onChange={selectEmployee}
              isClearable={false}
              isMulti
              styles={colourStyles}
            />
          </div>

          <div className="schedule-reg__time-pick">
            <div className="schedule-reg__label">2.출퇴근 시간</div>
            <div className="schedule-reg__time-pick__time">
              <div className="schedule-reg__time-pick__start">
                <TimeInput
                  value={form.startTime}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => timePicker(event, 'START')}
                  iconSide="RIGHT"
                />
              </div>
              <div className="schedule-reg__time-pick__dash">
                <DashIcon />
              </div>
              <div className="schedule-reg__time-pick__end">
                <TimeInput
                  value={form.endTime}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => timePicker(event, 'END')}
                  iconSide="RIGHT"
                />
              </div>
            </div>
          </div>

          <div className="schedule-reg__time-pick">
            <div className="schedule-reg__label">3.근무일 입력</div>
            <div className="schedule-reg__calendar-section">
              <div className="schedule-reg__calendar-section__cal">
                <Calendar
                  defaultValue={new Date()}
                  locale="ko-CA"
                  view="month"
                  className="cal"
                  calendarType="US"
                  formatDay={(locale, date) => dayjs(date).format('D')}
                  formatMonthYear={(locale, date) => dayjs(date).format('YYYY년 M월')}
                  onClickDay={pickSchedules}
                />
              </div>
              <div className="schedule-reg__calendar-section__additional-sche">
                <div className="schedule-reg__calendar-section__additional-sche__head">
                  <DotIcon /> 추가일정 목록 {schedules.length > 0 ? `(${schedules.length}개)` : ''}
                </div>
                <div className="schedule-reg__calendar-section__additional-sche__schedules">
                  {schedules.length > 0 &&
                    schedules
                      .sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0))
                      .map((sch) => (
                        <div key={sch.id} className="schedule-reg__calendar-section__additional-sche__label">
                          <Label
                            text={`${moment(sch.date).format('YYYY년 M월 D일')}`}
                            type={'type4'}
                            action={() => removeSchedules(sch.id)}
                          />
                        </div>
                      ))}
                </div>
              </div>
            </div>
          </div>

          <div className="schedule-reg__allowance-section">
            <div className="schedule-reg__label">4.수당포함여부</div>
            <div className="schedule-reg__allowance">
              <div
                className={
                  allowance === 'NOT_APPLICABLE'
                    ? 'schedule-reg__allowance__radio-active'
                    : 'schedule-reg__allowance__radio'
                }>
                <RadioButton text="미적용" checked={() => selectAllowance('9')} radioName={'allowance'} />
              </div>
              <div
                className={
                  allowance === 'OVERTIME_ALLOWANCE'
                    ? 'schedule-reg__allowance__radio-active'
                    : 'schedule-reg__allowance__radio'
                }>
                <RadioButton
                  text="초과근무수당 적용(1.5배)"
                  checked={() => selectAllowance('8')}
                  radioName={'allowance'}
                />
              </div>
              <div
                className={
                  allowance === 'NIGHT_SHIFT_ALLOWANCE'
                    ? 'schedule-reg__allowance__radio-active'
                    : 'schedule-reg__allowance__radio'
                }>
                <RadioButton
                  text="야간근무수당적용(1.5배)"
                  checked={() => selectAllowance('7')}
                  radioName={'allowance'}
                />
              </div>
            </div>
            <div className="schedule-reg__allowance">
              <div
                className={
                  allowance === 'OVERTIME_ALLOWANCE_DOUBLE'
                    ? 'schedule-reg__allowance__radio-active'
                    : 'schedule-reg__allowance__radio'
                }>
                <RadioButton
                  text="초과,야근근무수당 중복 적용 (2배)"
                  checked={() => selectAllowance('6')}
                  radioName={'allowance'}
                />
              </div>
            </div>
          </div>

          <div className="schedule-reg__note">
            *선택한 수당 적용은 급여유형이 시급직인 직원만 적용되며 다른 급여유형의 경우 "초과, 야근근무수당 중복
            적용(2배)"가 적용됩니다.
          </div>

          <div className="schedule-reg__buttons">
            <div className="schedule-reg__buttons__cancel">
              <Button label="취소" type="type13" />
            </div>
            <div className="schedule-reg__buttons__save">
              <Button label="등록" type="type2" action={clickSumbitBtn} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ScheduleRegModal;
